import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getApiToken,
  getUserSessionToken,
  isEnvApiTokenConfigured,
  shouldPutTokenInQuery,
  TOKEN_CHANGED_EVENT,
  isDashboardSameOriginHost,
} from '../api/auth';

const WS_PUSH_INTERVAL_MS = Number(process.env.REACT_APP_DASHBOARD_WS_INTERVAL_MS) || 500;
const REST_FALLBACK_MS = 15_000;
const WS_RECONNECT_MS = 3_000;
const REFRESH_DEBOUNCE_MS = 200;
const WS_LABEL_THROTTLE_MS = 1000;

/** In CRA dev, REST uses the proxy on :3000 but WS must hit FastAPI on :8585 directly. */
function resolveLocalDevWsBase() {
  if (typeof window === 'undefined') return null;
  const { hostname, port, protocol } = window.location;
  if (protocol !== 'http:' && protocol !== 'https:') return null;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') return null;
  // Only auto-bypass the proxy on the React dev server (not production builds).
  if (port && port !== '3000' && port !== '3001') return null;

  const proxy = (process.env.REACT_APP_PROXY_TARGET || '').trim();
  let apiPort = '8585';
  try {
    if (proxy) apiPort = new URL(proxy).port || apiPort;
  } catch {
    /* ignore */
  }
  const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
  // Match page hostname (localhost vs 127.0.0.1) — avoids browser cross-host quirks.
  return `${wsProto}//${hostname}:${apiPort}`;
}

function buildWsUrl(wsPath = '/ws/dashboard') {
  const token = getApiToken();
  const explicit = (process.env.REACT_APP_WS_URL || '').trim().replace(/\/$/, '');
  const localDev = resolveLocalDevWsBase();

  let base;
  if (isDashboardSameOriginHost() && typeof window !== 'undefined' && window.location?.host) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    base = `${proto}//${window.location.host}`;
  } else if (explicit) {
    base = explicit;
  } else if (localDev) {
    base = localDev;
  } else if (typeof window !== 'undefined' && window.location?.host) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    base = `${proto}//${window.location.host}`;
  } else {
    base = 'ws://127.0.0.1:8585';
  }

  const url = new URL(`${base}${wsPath}`);
  // Browsers cannot set auth headers on WebSocket — put token in query when needed.
  if (
    token &&
    (shouldPutTokenInQuery() ||
      isEnvApiTokenConfigured() ||
      Boolean(getUserSessionToken()))
  ) {
    url.searchParams.set('token', token);
  }
  return url.toString();
}

/** API / WS payloads use snake_case nested under floating, closed, orders. */
export function normalizeDashboardPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.floating && raw.closed) return raw;
  if (raw.data?.floating) return raw.data;
  return raw;
}

function buildFilterKey({
  historicalMode,
  segment,
  period,
  startDate,
  endDate,
  depositStartDate,
  depositEndDate,
}) {
  return [
    historicalMode ? 'hist' : 'live',
    segment,
    period,
    startDate ?? '',
    endDate ?? '',
    depositStartDate ?? '',
    depositEndDate ?? '',
  ].join('|');
}

function stampFilterKey(payload, filterKey) {
  if (!payload || typeof payload !== 'object') return payload;
  return { ...payload, __filterKey: filterKey };
}

function isLiveMt5Source(source) {
  return (
    source &&
    source !== 'mt5_webapi_loading' &&
    source !== 'mysql_replica'
  );
}

function canKeepPrevFloating(prev, filterKey) {
  return (
    prev?.floating?.per_login?.length > 0 &&
    prev?.__filterKey === filterKey
  );
}

/** True when closed/cash book has no login rows and zero totals. */
function isClosedBookEmpty(closed) {
  if (!closed || typeof closed !== 'object') return true;
  const hasRows =
    (closed.per_login?.length ?? 0) > 0 || (closed.per_login_cash?.length ?? 0) > 0;
  if (hasRows) return false;
  const totals = [
    closed.total_closed_pl,
    closed.total_deposit,
    closed.total_withdrawal,
    closed.total_commission,
    closed.total_swap,
  ].map((v) => Math.abs(Number(v) || 0));
  return totals.every((v) => v < 1e-9);
}

/**
 * REST MySQL often returns empty closed while WS live MT5 deal/get already
 * filled the table — never wipe a non-empty live closed book with empty REST.
 * Only keep prev when the filter key matches (WH/STP / period isolation).
 * Prefer the richer book so progressive fill (one-by-one) keeps growing.
 */
function pickClosedPayload(incoming, prev, filterKey) {
  const sameFilter =
    filterKey == null || prev?.__filterKey == null || prev.__filterKey === filterKey;
  if (incoming == null) {
    return sameFilter ? prev?.closed : undefined;
  }
  const incomingDay = String(incoming?.date_from || '').slice(0, 10);
  const prevDay = String(prev?.closed?.date_from || '').slice(0, 10);
  const incomingLive =
    typeof incoming?.period === 'string' && incoming.period.includes('live');
  const prevLag =
    typeof prev?.closed?.period === 'string' &&
    prev.closed.period.includes('replica lag');

  // Always take live MT5 book, or clear stale replica day when the day changes.
  if (incomingLive || prevLag || (incomingDay && prevDay && incomingDay !== prevDay)) {
    return incoming;
  }

  if (
    sameFilter &&
    isClosedBookEmpty(incoming) &&
    !isClosedBookEmpty(prev?.closed)
  ) {
    return prev.closed;
  }
  if (
    sameFilter &&
    !isClosedBookEmpty(incoming) &&
    !isClosedBookEmpty(prev?.closed)
  ) {
    const inRows =
      (incoming.per_login?.length ?? 0) + (incoming.per_login_cash?.length ?? 0);
    const prevRows =
      (prev.closed.per_login?.length ?? 0) +
      (prev.closed.per_login_cash?.length ?? 0);
    if (prevRows > inRows) return prev.closed;
    if (prevRows === inRows) {
      const score = (c) =>
        Math.abs(Number(c?.total_closed_pl || 0)) +
        Math.abs(Number(c?.total_deposit || 0)) +
        Math.abs(Number(c?.total_withdrawal || 0));
      if (score(prev.closed) > score(incoming)) return prev.closed;
    }
  }
  return incoming;
}

/** KPI total = sum of live per-login Profit (MT5 mirror). */
function mirrorFloatingTotal(floating) {
  const rows = floating?.per_login ?? [];
  if (!rows.length) return floating?.total_floating;
  return rows.reduce((sum, r) => sum + Number(r.profit || 0), 0);
}

function withMirrorTotal(floating) {
  if (!floating) return floating;
  const total = mirrorFloatingTotal(floating);
  if (total == null || !Number.isFinite(total)) return floating;
  return { ...floating, total_floating: total };
}

/** Prefer the more complete floating P/L when live ticks omit swap/storage. */
function pickFloatingProfit(prevProfit, liveProfit, { preferLive = false } = {}) {
  const prev = Number(prevProfit);
  const live = Number(liveProfit);
  if (liveProfit == null || Number.isNaN(live)) {
    return Number.isFinite(prev) ? prev : 0;
  }
  if (preferLive || prevProfit == null || Number.isNaN(prev)) return live;
  if (prev === 0) return live;
  if (Math.abs(live) > Math.abs(prev)) return live;
  if (live * prev > 0 && Math.abs(live) < Math.abs(prev)) return prev;
  return live;
}

/**
 * Live WS sends the full segment floating book (not a delta).
 * Keep only logins present in *live* — otherwise WH accounts that briefly leaked
 * into an STP push stay forever via merge-into-prev.
 */
function mergeLivePerLogin(prevRows, liveRows) {
  if (!liveRows?.length) return prevRows ?? liveRows;
  const prevMap = new Map((prevRows ?? []).map((r) => [r.login, r]));
  const out = [];
  for (const row of liveRows) {
    const prev = prevMap.get(row.login);
    const name = row.name || prev?.name;
    const profit = pickFloatingProfit(prev?.profit, row.profit, { preferLive: true });
    out.push({ ...prev, ...row, profit, ...(name ? { name } : {}) });
  }
  return out.sort(
    (a, b) => Math.abs(Number(b.profit) || 0) - Math.abs(Number(a.profit) || 0)
  );
}

function scaleSymbolsToLoginTotals(symbolRows, loginRows) {
  if (!symbolRows?.length || !loginRows?.length) return symbolRows ?? [];
  const targets = new Map(
    loginRows.map((r) => [Number(r.login), Number(r.profit || 0)])
  );
  const byLogin = new Map();
  for (const row of symbolRows) {
    const lg = Number(row.login);
    if (!lg) continue;
    if (!byLogin.has(lg)) byLogin.set(lg, []);
    byLogin.get(lg).push({ ...row });
  }
  const out = [];
  for (const [lg, rows] of byLogin) {
    const target = targets.get(lg);
    // Drop symbol rows for logins not in the current live book (segment filter).
    if (target == null || !Number.isFinite(target)) {
      continue;
    }
    const current = rows.reduce(
      (sum, r) => sum + Number(r.floating_pl || 0),
      0
    );
    if (!Number.isFinite(current) || Math.abs(current) < 1e-9) {
      out.push(...rows);
      continue;
    }
    const scale = target / current;
    for (const row of rows) {
      out.push({
        ...row,
        floating_pl: Math.round(Number(row.floating_pl || 0) * scale * 100) / 100,
      });
    }
  }
  return out.sort(
    (a, b) =>
      Math.abs(Number(b.floating_pl) || 0) - Math.abs(Number(a.floating_pl) || 0)
  );
}

function mergeLivePerSymbol(prevRows, liveRows) {
  if (!liveRows?.length) return prevRows ?? liveRows;
  const prevMap = new Map(
    (prevRows ?? []).map((r) => [`${r.login}-${r.symbol}`, r])
  );
  const out = [];
  for (const row of liveRows) {
    const key = `${row.login}-${row.symbol}`;
    const prev = prevMap.get(key);
    out.push({
      ...prev,
      ...row,
      floating_pl: pickFloatingProfit(prev?.floating_pl, row.floating_pl, {
        preferLive: true,
      }),
      closed_pl: row.closed_pl ?? prev?.closed_pl ?? 0,
    });
  }
  return out.sort(
    (a, b) =>
      Math.abs(Number(b.floating_pl) || 0) - Math.abs(Number(a.floating_pl) || 0)
  );
}

function mergeLiveFloating(prevFloating, liveFloating) {
  if (!liveFloating) return prevFloating ?? liveFloating;
  const liveSymbols = liveFloating.per_symbol ?? [];
  const prevSymbols = prevFloating?.per_symbol ?? [];
  const perLogin = mergeLivePerLogin(
    prevFloating?.per_login,
    liveFloating.per_login
  );
  if (!liveSymbols.length) {
    if (prevSymbols.length) {
      const scaled = scaleSymbolsToLoginTotals(
        prevSymbols,
        perLogin
      );
      return withMirrorTotal({
        ...liveFloating,
        per_login: perLogin,
        per_symbol: scaled,
      });
    }
    const closedByKey = new Map(
      prevSymbols.map((r) => [`${r.login}-${r.symbol}`, r.closed_pl ?? 0])
    );
    if (!closedByKey.size) {
      return withMirrorTotal({ ...liveFloating, per_login: perLogin });
    }
    return withMirrorTotal({
      ...liveFloating,
      per_login: perLogin,
      per_symbol: (liveFloating.per_symbol ?? []).map((r) => ({
        ...r,
        closed_pl: closedByKey.get(`${r.login}-${r.symbol}`) ?? r.closed_pl ?? 0,
      })),
    });
  }
  return withMirrorTotal({
    ...liveFloating,
    per_login: perLogin,
    per_symbol: mergeLivePerSymbol(prevSymbols, liveSymbols),
  });
}

function wsOverviewMatchesFilter(msg, filterKey, historicalMode, wsPath = '') {
  const parts = filterKey.split('|');
  const wantSegment = parts[1] ?? '';
  const wantPeriod = parts[2] ?? '';

  // MT5: match segment + period only (hub dates can lag; strict date match dropped floating).
  if (wsPath.includes('/mt5')) {
    return (msg.segment ?? '') === wantSegment && (msg.period ?? 'today') === wantPeriod;
  }

  const msgKey = buildFilterKey({
    historicalMode,
    segment: msg.segment ?? '',
    period: msg.period ?? 'today',
    startDate: msg.start_date ?? '',
    endDate: msg.end_date ?? '',
    depositStartDate: msg.deposit_start_date ?? '',
    depositEndDate: msg.deposit_end_date ?? '',
  });

  if (msg.start_date && msg.end_date) {
    return msgKey === filterKey;
  }

  if (msg.period) {
    return msgKey === filterKey;
  }

  return false;
}

/**
 * Live: WebSocket + REST. Historical / restOnly: REST only (one debounced fetch per filter).
 */
export function useDashboardSocket({
  segment,
  period,
  startDate,
  endDate,
  depositStartDate,
  depositEndDate,
  historicalMode = false,
  wsPath = '/ws/dashboard',
  wsIntervalMs,
  restOnly = false,
  restPollMs,
  enabled = true,
  reconnectOnFilterChange = false,
  skipRestWhenWsConnected = false,
  onFetch,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const [lastWsAt, setLastWsAt] = useState(null);
  const lastWsMarkRef = useRef(0);
  const wsRef = useRef(null);
  const wsConnGenRef = useRef(0);
  const subscribeMsgRef = useRef(null);
  const onFetchRef = useRef(onFetch);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const abortRef = useRef(null);
  const fetchGenRef = useRef(0);
  const loadingRef = useRef(false);
  const enabledRef = useRef(enabled);

  const filterKey = buildFilterKey({
    historicalMode,
    segment,
    period,
    startDate,
    endDate,
    depositStartDate,
    depositEndDate,
  });

  const filterKeyRef = useRef(filterKey);
  const historicalModeRef = useRef(historicalMode);
  const wsPathRef = useRef(wsPath);
  const skipRestWhenWsRef = useRef(skipRestWhenWsConnected);
  const prevFilterKeyRef = useRef(null);

  onFetchRef.current = onFetch;
  enabledRef.current = enabled;
  filterKeyRef.current = filterKey;
  historicalModeRef.current = historicalMode;
  wsPathRef.current = wsPath;
  skipRestWhenWsRef.current = skipRestWhenWsConnected;

  const subscribePayload = useCallback(() => {
    const msg = {
      action: 'subscribe',
      segment,
      period,
      interval_ms: wsIntervalMs ?? WS_PUSH_INTERVAL_MS,
    };
    if (startDate && endDate) {
      msg.start_date = startDate;
      msg.end_date = endDate;
    }
    if (depositStartDate && depositEndDate) {
      msg.deposit_start_date = depositStartDate;
      msg.deposit_end_date = depositEndDate;
    }
    return msg;
  }, [segment, period, startDate, endDate, depositStartDate, depositEndDate, wsIntervalMs]);

  useEffect(() => {
    subscribeMsgRef.current = subscribePayload();
  }, [subscribePayload]);

  const markWsUpdate = useCallback(() => {
    const now = Date.now();
    if (now - lastWsMarkRef.current < WS_LABEL_THROTTLE_MS) return;
    lastWsMarkRef.current = now;
    setLastWsAt(new Date(now));
  }, []);

  const refresh = useCallback(async ({ abortPrevious = true } = {}) => {
    const fetcher = onFetchRef.current;
    if (!fetcher) return null;

    if (abortPrevious) {
      abortRef.current?.abort();
    }
    const controller = new AbortController();
    if (abortPrevious || !abortRef.current) {
      abortRef.current = controller;
    }
    const gen = ++fetchGenRef.current;
    loadingRef.current = true;
    setLoading(true);

    try {
      const payload = normalizeDashboardPayload(
        await fetcher({ signal: controller.signal })
      );
      if (gen !== fetchGenRef.current) return null;
      if (payload) {
        const key = filterKeyRef.current;
          setData((prev) => {
            const emptyFloating = !payload.floating?.per_login?.length;
            const isMt5Loading = payload.data_source === 'mt5_webapi_loading';
            const prevWasLive = isLiveMt5Source(prev?.data_source);
            const wsHasLiveFloating =
              !historicalModeRef.current &&
              skipRestWhenWsRef.current &&
              wsRef.current?.readyState === WebSocket.OPEN &&
              (prevWasLive || prev?.floating?.per_login?.length);
            if (wsHasLiveFloating) {
              return stampFilterKey(
                {
                  ...prev,
                  closed: pickClosedPayload(payload.closed, prev, key),
                  orders: payload.orders ?? prev.orders,
                  message: payload.message ?? prev.message,
                },
                key
              );
            }
            if (
              isMt5Loading &&
              prevWasLive &&
              prev?.floating?.per_login?.length
            ) {
              return stampFilterKey(
                {
                  ...prev,
                  closed: pickClosedPayload(payload.closed, prev, key),
                  orders: payload.orders ?? prev.orders,
                  message: payload.message ?? prev.message,
                },
                key
              );
            }
            const keepPrev =
              !historicalModeRef.current &&
              emptyFloating &&
              (canKeepPrevFloating(prev, key) ||
                (isMt5Loading && prev?.floating?.per_login?.length) ||
                (isMt5Loading &&
                  wsRef.current?.readyState === WebSocket.OPEN &&
                  prev?.__filterKey === key));
            if (keepPrev) {
              const nextSource =
                payload.data_source &&
                payload.data_source !== 'mt5_webapi_loading'
                  ? payload.data_source
                  : prev?.data_source;
              return stampFilterKey(
                {
                  ...prev,
                  closed: pickClosedPayload(payload.closed, prev, key),
                  orders: payload.orders ?? prev.orders,
                  data_source: nextSource,
                  message: payload.message ?? prev.message,
                },
                key
              );
            }
          // Same-filter REST refresh: keep live closed if MySQL book is empty.
          if (
            prev?.__filterKey === key &&
            !isClosedBookEmpty(prev?.closed) &&
            isClosedBookEmpty(payload.closed)
          ) {
            return stampFilterKey(
              {
                ...payload,
                closed: prev.closed,
                floating: payload.floating?.per_login?.length
                  ? payload.floating
                  : prev.floating,
              },
              key
            );
          }
          return stampFilterKey(payload, key);
        });
        setError(null);
      }
      setLoading(false);
      loadingRef.current = false;
      return payload;
    } catch (e) {
      if (e.name === 'AbortError') return null;
      if (gen !== fetchGenRef.current) return null;
      setError(e.message || 'Failed to load dashboard');
      setLoading(false);
      loadingRef.current = false;
      return null;
    }
  }, []);

  const closeWs = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendSubscribe = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const msg = subscribeMsgRef.current ?? subscribePayload();
    ws.send(JSON.stringify(msg));
  }, [subscribePayload]);

  const connect = useCallback(() => {
    const apiToken = getApiToken();
    if (!apiToken) {
      setWsStatus('no_token');
      return;
    }
    if (restOnly) {
      setWsStatus('rest_only');
      return;
    }
    if (!enabledRef.current) {
      setWsStatus('paused');
      return;
    }

    const existing = wsRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    closeWs();
    setWsStatus('connecting');
    const wsUrl = buildWsUrl(wsPath);
    const tokenInQuery = (() => {
      try {
        return new URL(wsUrl).searchParams.has('token');
      } catch {
        return false;
      }
    })();
    const ws = new WebSocket(wsUrl);
    const connGen = ++wsConnGenRef.current;
    wsRef.current = ws;

    const connectTimeout = setTimeout(() => {
      if (wsConnGenRef.current !== connGen || ws.readyState === WebSocket.OPEN) return;
      ws.close();
      setWsStatus('error');
      setError(
        'WebSocket connection timed out — start pricing-fastAPI (python main.py on :8585), then refresh. ' +
          `Tried: ${wsUrl}`
      );
    }, 30_000);

    ws.onopen = () => {
      if (wsConnGenRef.current !== connGen) return;
      clearTimeout(connectTimeout);
      setWsStatus('connected');
      setError(null);
      const apiToken = getApiToken();
      if (apiToken && !tokenInQuery) {
        ws.send(JSON.stringify({ action: 'auth', token: apiToken }));
      }
      ws.send(JSON.stringify(subscribeMsgRef.current ?? subscribePayload()));
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 15_000);
    };

    ws.onmessage = (ev) => {
      if (wsConnGenRef.current !== connGen || wsRef.current !== ws) return;
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'hub_ready') {
          if (
            (msg.segment ?? '') !== (filterKeyRef.current.split('|')[1] ?? '')
          ) {
            return;
          }
          setLoading(false);
          setData((prev) =>
            stampFilterKey(
              {
                ...(prev || {}),
                data_source: msg.data_source || 'mt5_webapi_live',
              },
              filterKeyRef.current
            )
          );
          markWsUpdate();
          return;
        }
        if (msg.type === 'loading') {
          // Hub bootstrap — keep closed/orders visible; floating fills in via live push.
          return;
        }
        if (msg.type === 'overview') {
          if (historicalModeRef.current) return;
          if (
            !wsOverviewMatchesFilter(
              msg,
              filterKeyRef.current,
              historicalModeRef.current,
              wsPathRef.current
            )
          ) {
            return;
          }
          const payload = normalizeDashboardPayload(msg.data ?? msg);
          if (!payload?.floating && !payload?.closed) return;

          // MT5 tick stream: live floating + periodic closed/orders refresh.
          if (msg.live) {
            const key = filterKeyRef.current;
            if (payload.floating?.per_login?.length) {
              setLoading(false);
            }
            setData((prev) => {
              const wantSegment = key.split('|')[1] ?? '';
              if (msg.segment && wantSegment && msg.segment !== wantSegment) {
                return prev;
              }
              const hasFloating = Boolean(payload.floating?.per_login?.length);
              const hasClosed = Boolean(payload.closed);
              const hasOrders = Boolean(payload.orders);
              const hasTabTotals = Boolean(payload.segment_tab_totals);
              if (!hasFloating && !hasClosed && !hasOrders && !hasTabTotals) {
                return prev ? stampFilterKey(prev, key) : prev;
              }
              const floating = hasFloating
                ? withMirrorTotal(
                    mergeLiveFloating(prev?.floating, payload.floating)
                  )
                : prev?.floating;
              const segKey = msg.segment || wantSegment;
              let segmentTabTotals = prev?.segment_tab_totals;
              if (hasTabTotals) {
                segmentTabTotals = payload.segment_tab_totals;
              } else if (hasOrders && segKey && payload.orders) {
                const prevSeg = segmentTabTotals?.[segKey] || {};
                segmentTabTotals = {
                  ...(segmentTabTotals || {}),
                  [segKey]: {
                    ...prevSeg,
                    trading_accounts: payload.orders.segment_account_total,
                    open_positions: payload.orders.live_position_legs,
                    open_orders: payload.orders.open_orders_count,
                  },
                };
              }
              const nextSource =
                payload.data_source ||
                prev?.data_source ||
                'mt5_webapi_accounts';
              const next = {
                ...(prev || {}),
                ...(hasFloating ? { floating } : {}),
                ...(hasClosed
                  ? { closed: pickClosedPayload(payload.closed, prev, key) }
                  : {}),
                ...(hasOrders ? { orders: payload.orders } : {}),
                ...(segmentTabTotals ? { segment_tab_totals: segmentTabTotals } : {}),
                data_source: nextSource,
                __wsTs: msg.ts ?? Date.now(),
              };
              return stampFilterKey(next, key);
            });
            setError(null);
            markWsUpdate();
            return;
          }

          setLoading(false);
          const key = filterKeyRef.current;
          setData((prev) => {
            const emptyFloating = !payload.floating?.per_login?.length;
            const isMt5Loading = payload.data_source === 'mt5_webapi_loading';
            const prevWasLive = isLiveMt5Source(prev?.data_source);
            if (
              isMt5Loading &&
              prevWasLive &&
              prev?.floating?.per_login?.length
            ) {
              return stampFilterKey(
                {
                  ...prev,
                  closed: pickClosedPayload(payload.closed, prev, key),
                  orders: payload.orders ?? prev.orders,
                  message: payload.message ?? prev.message,
                },
                key
              );
            }
            const keepPrev =
              !historicalModeRef.current &&
              emptyFloating &&
              (canKeepPrevFloating(prev, key) ||
                (isMt5Loading && prev?.floating?.per_login?.length));
            if (keepPrev) {
              const nextSource =
                payload.data_source &&
                payload.data_source !== 'mt5_webapi_loading'
                  ? payload.data_source
                  : prev?.data_source;
              return stampFilterKey(
                {
                  ...payload,
                  floating: prev.floating,
                  closed: pickClosedPayload(payload.closed, prev, key),
                  orders: payload.orders ?? prev.orders,
                  data_source: nextSource,
                  message: payload.message ?? prev.message,
                },
                key
              );
            }
            if (
              prev?.__filterKey === key &&
              !isClosedBookEmpty(prev?.closed) &&
              isClosedBookEmpty(payload.closed)
            ) {
              return stampFilterKey(
                {
                  ...payload,
                  closed: prev.closed,
                  floating: payload.floating?.per_login?.length
                    ? payload.floating
                    : prev.floating,
                },
                key
              );
            }
            return stampFilterKey(payload, key);
          });
          setError(null);
          markWsUpdate();
        } else if (msg.type === 'error') {
          setError(msg.message || 'WebSocket error');
          setLoading(false);
        }
      } catch {
        /* ignore */
      }
    };

    ws.onerror = () => {
      if (wsConnGenRef.current !== connGen) return;
      clearTimeout(connectTimeout);
      setWsStatus('error');
      setError(
        'WebSocket handshake failed — nginx on this host is likely serving index.html for /ws/ ' +
          '(fix: add location /ws/ proxy to FastAPI :8585 before location /). ' +
          `Tried: ${wsUrl}`
      );
    };

    ws.onclose = () => {
      clearTimeout(connectTimeout);
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (wsRef.current === ws) {
        setWsStatus('disconnected');
        wsRef.current = null;
        if (skipRestWhenWsRef.current && !historicalMode && !restOnly && enabledRef.current) {
          refresh({ abortPrevious: false });
        }
        if (
          !historicalMode &&
          !restOnly &&
          enabledRef.current &&
          getApiToken()
        ) {
          reconnectTimerRef.current = setTimeout(() => connect(), WS_RECONNECT_MS);
        }
      }
    };
  }, [closeWs, markWsUpdate, wsPath, restOnly, historicalMode]);

  // Reconnect / refresh when user applies a new token from the UI
  useEffect(() => {
    const onTokenChange = () => {
      if (!historicalMode && enabledRef.current) {
        if (restOnly) refresh();
        else connect();
      }
    };
    window.addEventListener(TOKEN_CHANGED_EVENT, onTokenChange);
    return () => window.removeEventListener(TOKEN_CHANGED_EVENT, onTokenChange);
  }, [connect, refresh, historicalMode, restOnly]);

  // WebSocket lifecycle — only when live + WS mode + tab active
  useEffect(() => {
    if (historicalMode || restOnly) {
      closeWs();
      setWsStatus(historicalMode ? 'historical' : 'rest_only');
      return undefined;
    }
    if (!enabled) {
      closeWs();
      setWsStatus('paused');
      return undefined;
    }
    connect();
    return () => closeWs();
  }, [historicalMode, restOnly, enabled, connect, closeWs]);

  // REST bootstrap per filterKey (WS live keeps floating from socket; REST fills closed/orders).
  useEffect(() => {
    if (!enabled) return undefined;
    setData(null);
    setLoading(true);
    const timer = setTimeout(() => refresh(), REFRESH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filterKey, refresh, enabled, restOnly]);

  // Re-subscribe WS when filters change (MT5: full reconnect to drop stale hub loop).
  useEffect(() => {
    if (historicalMode || restOnly || !enabled) return;
    const prev = prevFilterKeyRef.current;
    prevFilterKeyRef.current = filterKey;
    if (prev && prev !== filterKey && reconnectOnFilterChange) {
      connect();
      return;
    }
    sendSubscribe();
  }, [
    filterKey,
    historicalMode,
    restOnly,
    enabled,
    reconnectOnFilterChange,
    sendSubscribe,
    connect,
  ]);

  // REST poll when restOnly (no WebSocket — e.g. MT4 MySQL)
  useEffect(() => {
    if (!restOnly || historicalMode || !enabled) return undefined;
    const ms = restPollMs ?? REST_FALLBACK_MS;
    const id = setInterval(() => refresh(), ms);
    return () => clearInterval(id);
  }, [restOnly, restPollMs, historicalMode, enabled, refresh]);

  // REST fallback poll — when skipRestWhenWsConnected, rely on WS only (refresh on disconnect).
  useEffect(() => {
    if (historicalMode || restOnly || !enabled) return undefined;
    if (skipRestWhenWsRef.current) return undefined;
    const ms = restPollMs ?? REST_FALLBACK_MS;
    const id = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      if (loadingRef.current) return;
      refresh({ abortPrevious: false });
    }, ms);
    return () => clearInterval(id);
  }, [historicalMode, restOnly, enabled, refresh, restPollMs]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    },
    []
  );

  const lastWsLabel = lastWsAt
    ? lastWsAt.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : null;

  const manualRefresh = useCallback(() => refresh(), [refresh]);

  return {
    data,
    loading,
    wsStatus,
    lastWsAt,
    lastWsLabel,
    error,
    reconnect: connect,
    refresh: manualRefresh,
  };
}
